import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { useAnnouncer } from '@/components/accessibility/AnnouncerContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Copy, Save, RotateCcw, Calculator as CalcIcon, ArrowRight, Check, TrendingUp, TrendingDown } from 'lucide-react';
import { calculateVAT, formatCurrency, copyToClipboard, historyManager } from '@/lib/vatUtils';
import CountrySelector from './CountrySelector';
import MultiRateTable from './MultiRateTable';

const VATCalculator = ({ countries, onCountrySelect, preselectedCountry }) => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const { announcePolite, announceAssertive } = useAnnouncer();
  
  // Refs for focus management
  const amountInputRef = useRef(null);
  const resultsRef = useRef(null);
  
  // State
  const [selectedCountry, setSelectedCountry] = useState(preselectedCountry || null);
  const [amount, setAmount] = useState('');
  const [vatRate, setVatRate] = useState(22);
  const [customRate, setCustomRate] = useState('');
  const [operation, setOperation] = useState('add');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showMultiRate, setShowMultiRate] = useState(false);

  // Get all rates for selected country
  const countryRates = useMemo(() => {
    if (!selectedCountry) return [22];
    return selectedCountry.all_rates || [selectedCountry.standard_rate];
  }, [selectedCountry]);

  // Sync with preselected country from URL
  useEffect(() => {
    if (preselectedCountry && preselectedCountry.code !== selectedCountry?.code) {
      setSelectedCountry(preselectedCountry);
      setVatRate(preselectedCountry.standard_rate);
      setCustomRate('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedCountry]);

  // Handle country change
  const handleCountryChange = useCallback((country) => {
    setSelectedCountry(country);
    if (country) {
      setVatRate(country.standard_rate);
      setCustomRate('');
      announcePolite(`${country.name} selected. Standard VAT rate is ${country.standard_rate}%`);
      // Notify parent for URL update
      if (onCountrySelect) {
        onCountrySelect(country);
      }
    }
  }, [announcePolite, onCountrySelect]);

  // Handle rate change
  const handleRateChange = useCallback((value) => {
    if (value === 'custom') {
      setVatRate(parseFloat(customRate) || 0);
    } else {
      setVatRate(parseFloat(value));
      setCustomRate('');
      announcePolite(`VAT rate set to ${value}%`);
    }
  }, [customRate, announcePolite]);

  // Handle custom rate
  const handleCustomRateChange = useCallback((value) => {
    setCustomRate(value);
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
      setVatRate(parsed);
    }
  }, []);

  // Calculate
  const calculate = useCallback(() => {
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast({ title: t('error'), description: t('invalidAmount'), variant: 'destructive' });
      announceAssertive(t('invalidAmount'));
      return;
    }
    
    const calcResult = calculateVAT(parsedAmount, vatRate, operation);
    setResult({
      ...calcResult,
      vatRate,
      operation,
      countryCode: selectedCountry?.code,
      countryName: selectedCountry?.name,
      currency: selectedCountry?.currency || 'EUR'
    });

    // Auto-save to history
    historyManager.add({
      amount: parseFloat(amount),
      ...calcResult,
      vatRate,
      operation,
      countryCode: selectedCountry?.code,
      countryName: selectedCountry?.name,
      currency: selectedCountry?.currency || 'EUR'
    });

    // Announce result to screen readers
    const currency = selectedCountry?.currency || 'EUR';
    const announcement = `${t('calculationComplete')}. ${t('netAmount')}: ${formatCurrency(calcResult.netAmount, currency, language)}. ${t('vatAmount')}: ${formatCurrency(calcResult.vatAmount, currency, language)} at ${vatRate}%. ${t('grossAmount')}: ${formatCurrency(calcResult.grossAmount, currency, language)}.`;
    announcePolite(announcement);

    // Focus on results for screen readers
    setTimeout(() => {
      resultsRef.current?.focus();
    }, 100);
  }, [amount, vatRate, operation, selectedCountry, t, toast, language, announcePolite, announceAssertive]);

  // Copy results (defined before keyboard shortcuts useEffect)
  const handleCopy = useCallback(async () => {
    if (!result) return;
    
    const currency = result.currency || 'EUR';
    const text = `${t('netAmount')}: ${formatCurrency(result.netAmount, currency, language)}
${t('vatAmount')} (${result.vatRate}%): ${formatCurrency(result.vatAmount, currency, language)}
${t('grossAmount')}: ${formatCurrency(result.grossAmount, currency, language)}`;
    
    await copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: t('copied'), duration: 1500 });
    announcePolite(t('resultCopied'));
  }, [result, t, language, toast, announcePolite]);

  // Reset (defined before keyboard shortcuts useEffect)
  const handleReset = useCallback(() => {
    setAmount('');
    setResult(null);
    setCopied(false);
    setSaved(false);
    amountInputRef.current?.focus();
    announcePolite('Calculator reset');
  }, [announcePolite]);

  // Save to history
  const handleSave = useCallback(() => {
    if (!result) return;
    
    historyManager.add({
      amount: parseFloat(amount),
      ...result
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    toast({ title: t('saved'), duration: 1500 });
    announcePolite(t('resultSaved'));
  }, [result, amount, toast, t, announcePolite]);

  // Auto-calculate on changes with debounce
  // This allows users to type full numbers without immediate calculation
  useEffect(() => {
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }
    
    // Debounce calculation by 500ms to allow typing
    const timeoutId = setTimeout(() => {
      calculate();
    }, 500);
    
    // Cleanup timeout on next change
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, vatRate, operation]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        if (e.key === 'Enter' && e.target.id === 'calculator-input') {
          e.preventDefault();
          calculate();
        }
        return;
      }
      
      if (e.key === 'c' && !e.ctrlKey && !e.metaKey && result) {
        handleCopy();
      } else if (e.key === 'r' && !e.ctrlKey && !e.metaKey) {
        handleReset();
      } else if (e.key === '/') {
        e.preventDefault();
        amountInputRef.current?.focus();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [calculate, result, handleCopy, handleReset]);

  const currency = selectedCountry?.currency || 'EUR';

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Calculator Input */}
        <Card className="shadow-lg" data-testid="calculator-card">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <CalcIcon className="w-5 h-5 text-primary" aria-hidden="true" />
              {t('calculator')}
            </CardTitle>
            <CardDescription id="calculator-description">
              {t('appDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6" aria-describedby="calculator-description">
            {/* Country Selector */}
            <div className="space-y-2">
              <Label htmlFor="country-selector" id="country-label">
                {t('selectCountry')}
              </Label>
              <CountrySelector
                countries={countries}
                selected={selectedCountry}
                onSelect={handleCountryChange}
                aria-labelledby="country-label"
              />
            </div>

            {/* Amount Input */}
            <div className="space-y-2">
              <Label htmlFor="calculator-input" id="amount-label">
                {t('amount')}
                <span className="text-destructive ml-1" aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </Label>
              <div className="relative">
                <Input
                  ref={amountInputRef}
                  id="calculator-input"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={t('enterAmount')}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="text-lg pr-16"
                  data-testid="amount-input"
                  aria-labelledby="amount-label"
                  aria-describedby="amount-hint"
                  aria-required="true"
                  aria-invalid={amount !== '' && (isNaN(parseFloat(amount)) || parseFloat(amount) <= 0)}
                />
                <span 
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium"
                  aria-hidden="true"
                >
                  {currency}
                </span>
              </div>
              <p id="amount-hint" className="text-xs text-muted-foreground">
                {t('pressEnter')}
              </p>
            </div>

            {/* VAT Rate */}
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">{t('vatRate')}</legend>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="VAT rate selection">
                {countryRates.map((rate) => (
                  <Button
                    key={rate}
                    variant={vatRate === rate && !customRate ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleRateChange(rate.toString())}
                    data-testid={`rate-btn-${rate}`}
                    role="radio"
                    aria-checked={vatRate === rate && !customRate}
                    aria-label={`${rate} percent${rate === selectedCountry?.standard_rate ? ' (standard rate)' : ''}`}
                  >
                    {rate}%
                  </Button>
                ))}
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder={t('customRate')}
                    value={customRate}
                    onChange={(e) => handleCustomRateChange(e.target.value)}
                    className="w-24 h-9"
                    data-testid="custom-rate-input"
                    aria-label="Custom VAT rate"
                  />
                  <span className="text-muted-foreground" aria-hidden="true">%</span>
                </div>
              </div>
            </fieldset>

            {/* Operation */}
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium">{t('operation')}</legend>
              <RadioGroup 
                value={operation} 
                onValueChange={(value) => {
                  setOperation(value);
                  announcePolite(`${value === 'add' ? t('addVat') : t('removeVat')} selected`);
                }} 
                className="grid grid-cols-1 gap-3"
                aria-label="Calculation operation"
              >
                <div 
                  className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent cursor-pointer transition-colors" 
                  data-testid="operation-add"
                >
                  <RadioGroupItem value="add" id="add" />
                  <div className="flex-1">
                    <Label htmlFor="add" className="cursor-pointer flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-500" aria-hidden="true" />
                      {t('addVat')}
                    </Label>
                    <p className="text-sm text-muted-foreground" id="add-desc">{t('addVatDesc')}</p>
                  </div>
                </div>
                <div 
                  className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent cursor-pointer transition-colors" 
                  data-testid="operation-remove"
                >
                  <RadioGroupItem value="remove" id="remove" />
                  <div className="flex-1">
                    <Label htmlFor="remove" className="cursor-pointer flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-blue-500" aria-hidden="true" />
                      {t('removeVat')}
                    </Label>
                    <p className="text-sm text-muted-foreground" id="remove-desc">{t('removeVatDesc')}</p>
                  </div>
                </div>
              </RadioGroup>
            </fieldset>

            {/* Calculate Button */}
            <Button 
              onClick={calculate} 
              className="w-full" 
              size="lg" 
              data-testid="calculate-btn"
              aria-describedby="calculator-description"
            >
              <CalcIcon className="w-4 h-4 mr-2" aria-hidden="true" />
              {t('calculate')}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        <Card 
          className={`shadow-lg transition-all duration-300 ${result ? 'ring-2 ring-primary/20' : ''}`} 
          data-testid="results-card"
          ref={resultsRef}
          tabIndex={-1}
          role="region"
          aria-label="Calculation results"
          aria-live="polite"
        >
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-primary" aria-hidden="true" />
                {t('results')}
              </span>
              {selectedCountry && (
                <Badge variant="outline" className="font-normal">
                  <span aria-hidden="true">{selectedCountry.flag}</span>
                  <span className="ml-1">{selectedCountry.tax_name}</span>
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-6">
                {/* Result Values */}
                <div className="space-y-4" role="list" aria-label="Calculation breakdown">
                  <div 
                    className="flex justify-between items-center p-4 rounded-lg bg-muted/50"
                    role="listitem"
                  >
                    <span className="text-muted-foreground" id="net-label">{t('netAmount')}</span>
                    <span 
                      className="text-2xl font-bold" 
                      data-testid="result-net"
                      aria-labelledby="net-label"
                    >
                      {formatCurrency(result.netAmount, currency, language)}
                    </span>
                  </div>
                  
                  <div 
                    className="flex justify-between items-center p-4 rounded-lg bg-primary/10 border border-primary/20"
                    role="listitem"
                  >
                    <span className="text-muted-foreground flex items-center gap-2" id="vat-label">
                      {t('vatAmount')}
                      <Badge variant="secondary" aria-label={`at ${result.vatRate} percent`}>
                        {result.vatRate}%
                      </Badge>
                    </span>
                    <span 
                      className="text-2xl font-bold text-primary" 
                      data-testid="result-vat"
                      aria-labelledby="vat-label"
                    >
                      {formatCurrency(result.vatAmount, currency, language)}
                    </span>
                  </div>
                  
                  <div 
                    className="flex justify-between items-center p-4 rounded-lg bg-muted/50"
                    role="listitem"
                  >
                    <span className="text-muted-foreground" id="gross-label">{t('grossAmount')}</span>
                    <span 
                      className="text-2xl font-bold" 
                      data-testid="result-gross"
                      aria-labelledby="gross-label"
                    >
                      {formatCurrency(result.grossAmount, currency, language)}
                    </span>
                  </div>
                </div>

                <Separator />

                {/* Actions */}
                <div className="flex flex-wrap gap-2" role="toolbar" aria-label="Result actions">
                  <Button 
                    variant="outline" 
                    onClick={handleCopy} 
                    className="flex-1" 
                    data-testid="copy-btn"
                    aria-label={copied ? t('copied') : `${t('copy')} results to clipboard`}
                    aria-pressed={copied}
                  >
                    {copied ? <Check className="w-4 h-4 mr-2" aria-hidden="true" /> : <Copy className="w-4 h-4 mr-2" aria-hidden="true" />}
                    {copied ? t('copied') : t('copy')}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleSave} 
                    className="flex-1" 
                    data-testid="save-btn"
                    aria-label={saved ? t('saved') : `${t('save')} to history`}
                    aria-pressed={saved}
                  >
                    {saved ? <Check className="w-4 h-4 mr-2" aria-hidden="true" /> : <Save className="w-4 h-4 mr-2" aria-hidden="true" />}
                    {saved ? t('saved') : t('save')}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleReset} 
                    data-testid="reset-btn"
                    aria-label={`${t('reset')} calculator`}
                  >
                    <RotateCcw className="w-4 h-4" aria-hidden="true" />
                    <span className="sr-only">{t('reset')}</span>
                  </Button>
                </div>

                {/* Show Multi-Rate Toggle */}
                {selectedCountry && countryRates.length > 1 && (
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => setShowMultiRate(!showMultiRate)}
                    data-testid="toggle-multirate"
                    aria-expanded={showMultiRate}
                    aria-controls="multirate-table-section"
                  >
                    {showMultiRate ? 'Hide' : 'Show'} {t('multiRateComparison')}
                  </Button>
                )}
              </div>
            ) : (
              <div 
                className="flex flex-col items-center justify-center h-64 text-muted-foreground"
                role="status"
              >
                <CalcIcon className="w-16 h-16 mb-4 opacity-20" aria-hidden="true" />
                <p>{t('enterAmount')}</p>
                <p className="text-sm mt-2">{t('pressEnter')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Multi-Rate Table */}
      <div id="multirate-table-section">
        {showMultiRate && selectedCountry && result && (
          <MultiRateTable
            country={selectedCountry}
            amount={parseFloat(amount)}
            operation={operation}
          />
        )}
      </div>
    </div>
  );
};

export default VATCalculator;
